import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useLang } from "@/contexts/LangContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useSiteCopy } from "@/hooks/useSiteCopy";
import { api, resolveImage } from "@/lib/api";
import { ArrowUpRight, MoveRight } from "lucide-react";
import MaskedLines from "@/components/MaskedLines";
import Reveal, { RevealStagger, revealItem } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const HERO_IMG =
  "https://images.pexels.com/photos/29219960/pexels-photo-29219960.jpeg";
const WORKSHOP_IMG =
  "https://images.pexels.com/photos/10316634/pexels-photo-10316634.jpeg";

export default function Home() {
  const { t, lang, pick } = useLang();
  const { settings } = useSettings();
  const copy = useSiteCopy();
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const heroImgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    api
      .get("/projects", { params: { featured: true } })
      .then(({ data }) => setFeatured(data.slice(0, 3)))
      .catch(() => setFeatured([]));
  }, []);

  const heroTitleText = copy("hero_title", "hero.title");
  const heroSubtitleText = copy("hero_subtitle", "hero.subtitle");

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-[100vh] flex items-center overflow-hidden"
        style={{ position: "relative" }}
      >
        {/* Parallax background image */}
        <motion.div
          className="absolute inset-0 will-change-transform"
          style={{ y: heroImgY, scale: heroImgScale }}
        >
          <img
            src={HERO_IMG}
            alt="Spiral oak staircase"
            className="w-full h-full object-cover"
          />
        </motion.div>
        {/* Gradients */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#131211] via-[#131211]/80 to-transparent pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#131211] via-transparent to-[#131211]/40 pointer-events-none" />

        {/* Grain */}
        <div
          className="absolute inset-0 pointer-events-none opacity-30 mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.85 0 0 0 0 0.7 0 0 0 0 0.5 0 0 0 0.12 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />

        {/* Vertical brand line — draws down on load */}
        <motion.div
          className="hidden md:block absolute left-8 top-24 bottom-24 w-px bg-brand origin-top"
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 1.6, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
        />
        <motion.div
          className="hidden md:block absolute left-6 top-24 text-eyebrow text-brand [writing-mode:vertical-rl] rotate-180"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.4 }}
        >
          Est. MMXX · London
        </motion.div>

        <motion.div
          className="relative w-full max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-24"
          style={{ y: heroTextY, opacity: heroOpacity }}
        >
          <div className="max-w-3xl">
            <motion.p
              data-testid="hero-eyebrow"
              className="text-eyebrow text-brand mb-8"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {t("hero.eyebrow")}
            </motion.p>
            <h1
              data-testid="hero-title"
              className="font-display font-light text-[13vw] sm:text-7xl md:text-8xl lg:text-[7.2rem] xl:text-[8rem] text-ink leading-[0.9] tracking-[-0.03em]"
            >
              <MaskedLines
                text={heroTitleText.replace(/\\n/g, "\n")}
                delay={0.4}
                stagger={0.16}
                duration={1.1}
              />
            </h1>
            <motion.p
              className="mt-10 text-base sm:text-lg text-ink-muted font-light leading-relaxed max-w-xl"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
            >
              {heroSubtitleText}
            </motion.p>
            <motion.div
              className="mt-12 flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.5 }}
            >
              <MotionButton to="/consultations" data-testid="hero-cta-primary" primary>
                <span>{t("hero.ctaPrimary")}</span>
                <MoveRight size={18} />
              </MotionButton>
              <MotionButton to="/portfolio" data-testid="hero-cta-secondary">
                <span>{t("hero.ctaSecondary")}</span>
                <ArrowUpRight size={18} />
              </MotionButton>
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom indicator */}
        <motion.div
          className="absolute bottom-8 right-8 hidden md:flex items-center gap-3 text-eyebrow text-ink-dim"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.8 }}
        >
          <span>Scroll</span>
          <motion.span
            className="w-8 h-px bg-brand"
            animate={{ scaleX: [0.3, 1, 0.3] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: "left" }}
          />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <Marquee
        items={[
          "Bespoke Joinery",
          "Private Construction",
          "3D Design",
          "Since 2000",
          "London Ateliers",
        ]}
      />

      {/* INTRO */}
      <section className="relative py-32 md:py-40 px-6 sm:px-12 lg:px-24 max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
          <Reveal className="md:col-span-5 md:sticky md:top-32" y={40}>
            <p className="text-eyebrow text-brand mb-6">{t("intro.eyebrow")}</p>
            <h2 className="font-display font-light text-4xl sm:text-5xl text-ink leading-tight tracking-tight">
              {copy("intro_title", "intro.title")}
            </h2>
          </Reveal>
          <Reveal className="md:col-span-7" y={40} delay={0.15}>
            <p className="text-lg text-ink-muted font-light leading-relaxed">
              {copy("intro_body", "intro.body")}
            </p>
            <div className="mt-14 grid grid-cols-3 gap-6 sm:gap-12">
              <Stat n="25+" label={lang === "en" ? "Years" : "Años"} />
              <Stat n="180+" label={lang === "en" ? "Projects" : "Proyectos"} />
              <Stat n="03" label={lang === "en" ? "Disciplines" : "Disciplinas"} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="relative py-16 md:py-24 px-6 sm:px-12 lg:px-24 max-w-[1440px] mx-auto">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="text-eyebrow text-brand mb-4">{t("services.eyebrow")}</p>
            <h2 className="font-display font-light text-4xl sm:text-5xl text-ink tracking-tight">
              {t("services.title")}
            </h2>
          </div>
        </Reveal>

        <RevealStagger className="grid grid-cols-1 md:grid-cols-12 auto-rows-fr gap-6">
          {/* Carpentry — large */}
          <motion.div variants={revealItem} className="md:col-span-8 md:row-span-2">
            <ServiceCard
              to="/services#carpentry"
              testId="service-card-carpentry"
              num="01"
              image="https://images.pexels.com/photos/8146143/pexels-photo-8146143.jpeg"
              title={t("services.carpentry.title")}
              tag={t("services.carpentry.tag")}
              body={t("services.carpentry.body")}
              size="lg"
              ctaLabel={t("services.cta")}
            />
          </motion.div>

          {/* Construction */}
          <motion.div variants={revealItem} className="md:col-span-4">
            <ServiceCard
              to="/services#construction"
              testId="service-card-construction"
              num="02"
              image="https://images.pexels.com/photos/18729404/pexels-photo-18729404.jpeg"
              title={t("services.construction.title")}
              tag={t("services.construction.tag")}
            />
          </motion.div>

          {/* Consultations */}
          <motion.div variants={revealItem} className="md:col-span-4">
            <ServiceCard
              to="/consultations"
              testId="service-card-consult"
              num="03"
              image="https://images.pexels.com/photos/13727724/pexels-photo-13727724.png"
              title={t("services.consult.title")}
              tag={t("services.consult.tag")}
            />
          </motion.div>
        </RevealStagger>
      </section>

      {/* FEATURED PORTFOLIO */}
      <section className="relative py-32 md:py-40 px-6 sm:px-12 lg:px-24 max-w-[1440px] mx-auto">
        <Reveal className="flex items-end justify-between mb-16">
          <div>
            <p className="text-eyebrow text-brand mb-4">{t("portfolio.eyebrow")}</p>
            <h2 className="font-display font-light text-4xl sm:text-5xl text-ink tracking-tight">
              {t("portfolio.title")}
            </h2>
          </div>
          <Link
            to="/portfolio"
            data-testid="home-view-all-projects"
            className="hidden sm:inline-flex items-center gap-2 text-sm text-ink hover:text-brand transition-colors underline-link"
          >
            {t("portfolio.viewAll")} <ArrowUpRight size={16} />
          </Link>
        </Reveal>

        <RevealStagger className="grid grid-cols-1 md:grid-cols-3 gap-6" staggerDelay={0.12}>
          {featured.map((p, i) => (
            <motion.div key={p.id} variants={revealItem}>
              <Link
                to="/portfolio"
                data-testid={`home-featured-${i}`}
                className="group block"
              >
                <div className="relative overflow-hidden h-[460px] bg-bg-surface border border-edge group-hover:border-brand transition-colors">
                  <motion.img
                    src={resolveImage(p.cover_image)}
                    alt={pick(p, "title")}
                    className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0"
                    initial={{ scale: 1 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                  />
                  <div className="absolute top-4 left-4 text-eyebrow text-brand bg-[#131211]/70 backdrop-blur-sm px-2 py-1">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="mt-6">
                  <p className="text-eyebrow text-ink-dim mb-2">
                    {p.location} · {p.year}
                  </p>
                  <h3 className="font-display text-2xl text-ink group-hover:text-brand transition-colors">
                    {pick(p, "title")}
                  </h3>
                </div>
              </Link>
            </motion.div>
          ))}
        </RevealStagger>
      </section>

      {/* FEATURED VIDEO */}
      {settings.featured_video_id && (
        <section
          className="relative py-24 px-6 sm:px-12 lg:px-24 max-w-[1440px] mx-auto"
          data-testid="home-featured-video"
        >
          <Reveal className="mb-12 flex items-end justify-between">
            <div>
              <p className="text-eyebrow text-brand mb-4">
                {lang === "en" ? "Featured Film" : "Video Destacado"}
              </p>
              <h2 className="font-display font-light text-4xl sm:text-5xl text-ink tracking-tight">
                {lang === "en"
                  ? settings.featured_video_title_en || "Inside the workshop"
                  : settings.featured_video_title_es || "Dentro del taller"}
              </h2>
            </div>
            {settings.youtube_url && (
              <a
                href={settings.youtube_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center gap-2 text-sm text-ink hover:text-brand transition-colors underline-link"
              >
                {lang === "en" ? "Visit channel" : "Visitar canal"}{" "}
                <ArrowUpRight size={16} />
              </a>
            )}
          </Reveal>
          <Reveal>
            {settings.featured_video_is_short ? (
              <div className="flex justify-center">
                <div className="relative w-full max-w-[380px] aspect-[9/16] border border-edge bg-bg-surface overflow-hidden">
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${settings.featured_video_id}?rel=0&modestbranding=1&loop=1&playlist=${settings.featured_video_id}`}
                    title="Featured short"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            ) : (
              <div className="relative aspect-video w-full border border-edge bg-bg-surface overflow-hidden">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${settings.featured_video_id}?rel=0&modestbranding=1`}
                  title="Featured video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </Reveal>
        </section>
      )}

      {/* WORKSHOP QUOTE STRIP */}
      <WorkshopStrip lang={lang} />
    </div>
  );
}

/* ---------- Sub-components ---------- */

function MotionButton({ to, children, primary, ...rest }) {
  return (
    <Link
      to={to}
      {...rest}
      className={`group relative inline-flex items-center justify-between gap-8 px-7 py-4 min-w-[260px] overflow-hidden transition-colors ${
        primary
          ? "bg-brand text-ink-inverse hover:bg-brand-hover"
          : "border border-edge text-ink hover:border-brand hover:text-brand"
      }`}
    >
      <span className="relative z-10 text-sm font-medium tracking-wide">
        {children[0]}
      </span>
      <span className="relative z-10 transition-transform group-hover:translate-x-1">
        {children[1]}
      </span>
    </Link>
  );
}

function ServiceCard({ to, testId, num, image, title, tag, body, size, ctaLabel }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`group relative block overflow-hidden bg-bg-surface border border-edge hover:border-brand transition-colors ${
        size === "lg" ? "h-[420px] md:h-[640px]" : "h-[300px] md:h-[308px]"
      }`}
    >
      <motion.img
        src={image}
        alt=""
        className={`absolute inset-0 w-full h-full object-cover ${
          size === "lg" ? "opacity-70" : "opacity-30"
        }`}
        initial={{ scale: 1 }}
        whileHover={{ scale: 1.06 }}
        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
      />
      <div
        className={
          size === "lg"
            ? "absolute inset-0 bg-gradient-to-t from-[#131211] via-[#131211]/40 to-transparent"
            : "absolute inset-0 bg-gradient-to-br from-[#1A1918] to-[#131211]/70"
        }
      />
      <div className={`relative h-full flex flex-col justify-end ${size === "lg" ? "p-8 md:p-12" : "p-8"}`}>
        <p className="text-eyebrow text-brand mb-3">{num}</p>
        <h3
          className={`font-display text-ink mb-3 tracking-tight ${
            size === "lg" ? "text-3xl md:text-5xl" : "text-2xl md:text-3xl"
          }`}
        >
          {title}
        </h3>
        <p className="text-eyebrow text-ink-muted">{tag}</p>
        {body && size === "lg" && (
          <>
            <p className="mt-5 text-ink-muted max-w-md text-sm leading-relaxed">
              {body}
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-brand text-sm">
              {ctaLabel} <ArrowUpRight size={16} />
            </span>
          </>
        )}
      </div>
    </Link>
  );
}

function Stat({ n, label }) {
  const numRef = useRef(null);
  return (
    <Reveal y={16}>
      <div ref={numRef}>
        <div className="font-display font-light text-4xl sm:text-6xl text-brand leading-none">
          {n}
        </div>
        <div className="text-eyebrow text-ink-muted mt-3">{label}</div>
      </div>
    </Reveal>
  );
}

function WorkshopStrip({ lang }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);
  const smoothY = useSpring(y, { stiffness: 60, damping: 20 });

  return (
    <section ref={ref} className="relative h-[70vh] overflow-hidden">
      <motion.div className="absolute inset-0 w-full h-[130%]" style={{ y: smoothY }}>
        <img src={WORKSHOP_IMG} alt="Workshop" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-[#131211]/75" />
      <div className="relative h-full flex items-center justify-center text-center px-6">
        <Reveal>
          <p className="text-eyebrow text-brand mb-6">
            {lang === "en" ? "Since 2000" : "Desde 2000"}
          </p>
          <h2 className="font-display font-light italic text-3xl sm:text-5xl md:text-6xl text-ink max-w-3xl mx-auto leading-tight">
            {lang === "en"
              ? '"Measure twice. Cut once. Build something the next century can keep."'
              : '"Mide dos veces. Corta una. Construye algo que se quede el próximo siglo."'}
          </h2>
        </Reveal>
      </div>
    </section>
  );
}
